import React, { useState, useEffect, useRef } from 'react';
import {
    Shield, UserCheck, UserX, Edit2, StopCircle,
    PlayCircle, Search, Filter, X, Plus
} from 'lucide-react';
import CommonMultiSelect from '../../Common/CommonMultiSelect';
import Table from '../../Table/Table';
import CommonPagination from '../../Common/CommonPagination';
import Loading from '../../Common/Loading';
import RoleModal from './RoleModal';
import RoleUsersModal from './RoleUsersModal';
import { Tooltip } from '../../Common/Tooltip';
import ConfirmModal from '../../Common/ConfirmModal';
import Toast, { ToastType } from '../../Common/Toast';
import roleService, { Role, RoleStats } from '../../../services/role.service';
import modulesService, { Module } from '../../../services/modules.service';
import privilegeService, { Privilege } from '../../../services/privilege.service';
// import '../UserManagement/UserManagement.css';
import styles from './RoleManagement.module.css';
import authService from '../../../services/auth.service';
import organisationService from '../../../services/organisation.service';

// ─────────────────────────────────────────────────────────────────────────────
// ROLE HELPERS  (same pattern as UserManagement)
// ─────────────────────────────────────────────────────────────────────────────
type CallerRole = 'SUPER_ADMIN' | 'SUPER_ADMIN_ORG' | 'ORGANISATION_ADMIN' | 'CLIENT_ADMIN' | 'USER';

function resolveCallerRole(user: any): CallerRole {
    if (!user) return 'USER';
    const roleName: string = user.role?.name ?? '';
    if (user.is_superuser || roleName === 'SUPER_ADMIN')
        return user.organisation_id ? 'SUPER_ADMIN_ORG' : 'SUPER_ADMIN';
    if (roleName === 'ORGANISATION_ADMIN') return 'ORGANISATION_ADMIN';
    if (roleName === 'CLIENT_ADMIN' || user.is_client) return 'CLIENT_ADMIN';
    return 'USER';
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const RoleManagement: React.FC = () => {
    const currentUser  = authService.getUser();
    const callerRole   = resolveCallerRole(currentUser);

    const isSuperAdmin = callerRole === 'SUPER_ADMIN' || callerRole === 'SUPER_ADMIN_ORG';
    const isOrgAdmin   = callerRole === 'ORGANISATION_ADMIN';
    const canFilterOrg = isSuperAdmin;

    // ── Permission flags (loaded from API, mirrors UserManagement) ────────────
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [modules, setModules]                     = useState<Module[]>([]);

    useEffect(() => {
        const loadPermissions = async () => {
            try {
                const user = authService.getUser();
                if (!user?.email) return;
                const data = await modulesService.getUserModules(user.email);
                setModules(data);
            } catch (err) {
                console.error('Failed to load permissions:', err);
            } finally {
                setPermissionsLoaded(true);
            }
        };
        loadPermissions();
    }, []);

    // Adjust the module/submodule name to whatever key your backend uses for roles
    const roleModule = modules
        .find(m => m.name === 'users_permissions')
        ?.submodules?.find(sm => sm.name === 'role_module');

    const canViewRole   = roleModule?.privileges?.includes('READ')   ?? false;
    const canCreateRole = roleModule?.privileges?.includes('CREATE') ?? false;
    const canEditRole   = roleModule?.privileges?.includes('UPDATE') ?? false;
    const canToggleRole = roleModule?.privileges?.includes('DELETE') ?? false;

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage,   setCurrentPage]   = useState(0);
    const [itemsPerPage,  setItemsPerPage]  = useState(10);

    // ── Data ─────────────────────────────────────────────────────────────────
    const [roles,       setRoles]       = useState<Role[]>([]);
    const [stats,       setStats]       = useState<RoleStats | null>(null);
    const [totalRoles,  setTotalRoles]  = useState(0);
    const [loading,     setLoading]     = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // ── Modals ────────────────────────────────────────────────────────────────
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [editingRole,   setEditingRole]   = useState<Role | null>(null);
    const [modulesList,   setModulesList]   = useState<Module[]>([]);
    const [privileges,    setPrivileges]    = useState<Privilege[]>([]);
    const [statusFilter,  setStatusFilter]  = useState<string | null>(null);
    const [confirmModal,  setConfirmModal]  = useState<{ isOpen: boolean; role: Role | null }>({ isOpen: false, role: null });
    const [toast,         setToast]         = useState<{ message: string; type: ToastType } | null>(null);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [searchQuery,   setSearchQuery]   = useState('');
    const [selectedOrg,   setSelectedOrg]   = useState<string[]>([]);
    const [orgOptions,    setOrgOptions]    = useState<any[]>([]);
    const [showFilters,   setShowFilters]   = useState(false);

    // ── Refs ──────────────────────────────────────────────────────────────────
    const dataLoadingRef   = useRef(false);
    const modulesLoadedRef = useRef(false);

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD DATA
    // ─────────────────────────────────────────────────────────────────────────
    const loadData = async () => {
        if (!permissionsLoaded) return;
        if (!canViewRole) { setIsInitialLoading(false); return; }
        if (dataLoadingRef.current) return;
        dataLoadingRef.current = true;
        try {
            setLoading(true);
            const [rolesData, statsData] = await Promise.all([
                roleService.getRoles(
                    currentPage + 1,
                    itemsPerPage,
                    statusFilter || undefined,
                    searchQuery  || undefined,
                    selectedOrg.length > 0 ? selectedOrg : undefined,
                ),
                roleService.getRoleStats(),
            ]);
            setRoles(rolesData.roles);
            setTotalRoles(rolesData.total);
            setStats(statsData);
        } catch (err) {
            console.error('Failed to load roles:', err);
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
            dataLoadingRef.current = false;
        }
    };

    // Debounced search
    useEffect(() => {
        const id = setTimeout(() => { setCurrentPage(0); loadData(); }, 500);
        return () => clearTimeout(id);
    }, [searchQuery, selectedOrg]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!permissionsLoaded) return;
        loadData();
    }, [permissionsLoaded, currentPage, itemsPerPage, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─────────────────────────────────────────────────────────────────────────
    // LOAD MODULES / PRIVILEGES (lazy – only when modal opens)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isModalOpen && !modulesLoadedRef.current && modulesList.length === 0) {
            modulesLoadedRef.current = true;
            (async () => {
                try {
                    const [mod, priv] = await Promise.all([
                        modulesService.getAllModules(),
                        privilegeService.getPrivileges(),
                    ]);
                    setModulesList(mod);
                    setPrivileges(priv);
                } catch (err) {
                    console.error('Failed to load modules/privileges', err);
                    modulesLoadedRef.current = false;
                }
            })();
        }
    }, [isModalOpen, modulesList.length]);

    // ─────────────────────────────────────────────────────────────────────────
    // FILTER OPTIONS
    // ─────────────────────────────────────────────────────────────────────────
    const loadFilterOptions = async () => {
        try {
            if (canFilterOrg) {
                const response = await organisationService.getOrganisations(1, 100);
                if (response?.organisations) {
                    setOrgOptions(response.organisations.map((org: any) => ({
                        value: org.id,
                        label: org.name,
                    })));
                }
            }
        } catch (err) {
            console.error('Failed to load filter options:', err);
        }
    };

    const handleOpenFilters = () => {
        if (!showFilters) loadFilterOptions();
        setShowFilters(!showFilters);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIONS
    // ─────────────────────────────────────────────────────────────────────────
    const handleEdit = (role: Role) => {
        if (!canEditRole) {
            setToast({ message: 'You do not have permission to edit roles', type: 'warning' });
            return;
        }
        if (!role.can_edit) {
            setToast({ message: 'System roles cannot be edited', type: 'warning' });
            return;
        }
        setEditingRole(role);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        if (!canCreateRole) {
            setToast({ message: 'You do not have permission to create roles', type: 'warning' });
            return;
        }
        setEditingRole(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => { setIsModalOpen(false); setEditingRole(null); };

    const handleModalSubmit = async (data: {
        name: string;
        description: string;
        modules: Array<{ module_id?: string; submodule_id?: string; privilege_id: string }>;
    }) => {
        try {
            if (editingRole) {
                await roleService.updateRole(editingRole.id, data);
                setToast({ message: 'Role updated successfully', type: 'success' });
            } else {
                await roleService.createRole(data);
                setToast({ message: 'Role created successfully', type: 'success' });
            }
            handleModalClose();
            loadData();
        } catch (err: any) {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to save role';
            setToast({ message: msg, type: 'error' });
        }
    };

    const handleToggleStatus = (role: Role) => {
        if (!canToggleRole) {
            setToast({ message: 'You do not have permission to change role status', type: 'warning' });
            return;
        }
        if (!role.can_edit) {
            setToast({ message: 'System roles cannot be modified', type: 'warning' });
            return;
        }
        if (role.statusCode === 'ACTIVE') { setConfirmModal({ isOpen: true, role }); return; }
        toggleRoleStatus(role);
    };

    const toggleRoleStatus = async (role: Role) => {
        try {
            const newStatus = role.statusCode === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            await roleService.updateRole(role.id, { status_id: newStatus });
            loadData();
        } catch (err) {
            console.error('Failed to update role status:', err);
            setToast({ message: 'Failed to update role status', type: 'error' });
        }
    };

    const handleConfirmDeactivate = async () => {
        if (confirmModal.role) { await toggleRoleStatus(confirmModal.role); }
        setConfirmModal({ isOpen: false, role: null });
    };

    const handleStatClick = (type: 'total' | 'active' | 'inactive') => {
        setStatusFilter(type === 'total' ? null : type === 'active' ? 'ACTIVE' : 'INACTIVE');
        setCurrentPage(0);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // STAT CARDS
    // ─────────────────────────────────────────────────────────────────────────
    const roleStats = [
        { title: 'Total Roles',    value: stats?.total_roles.toString()    ?? '0', icon: Shield,    color: 'blue',   onClick: () => handleStatClick('total'),    active: statusFilter === null },
        { title: 'Active Roles',   value: stats?.active_roles.toString()   ?? '0', icon: UserCheck, color: 'green',  onClick: () => handleStatClick('active'),   active: statusFilter === 'ACTIVE' },
        { title: 'Inactive Roles', value: stats?.inactive_roles.toString() ?? '0', icon: UserX,     color: 'orange', onClick: () => handleStatClick('inactive'), active: statusFilter === 'INACTIVE' },
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // USERS MODAL
    // ─────────────────────────────────────────────────────────────────────────
    const [usersModalOpen,       setUsersModalOpen]       = useState(false);
    const [selectedRoleForUsers, setSelectedRoleForUsers] = useState<{ id: string; name: string } | null>(null);

    const handleUsersClick = (role: Role) => {
        setSelectedRoleForUsers({ id: role.id, name: role.name });
        setUsersModalOpen(true);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // TABLE COLUMNS  (permission-gated)
    // ─────────────────────────────────────────────────────────────────────────
    const roleColumns = [
        { key: 'name',        header: 'Role Name'   },
        { key: 'description', header: 'Description' },

        // "Created By" – SUPER_ADMIN + ORG_ADMIN
        ...(isSuperAdmin || isOrgAdmin ? [{
            key: 'created_by_name',
            header: 'Created By',
            render: (_: any, row: Role) =>
                row.created_by_name || (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                        {row.organisation_name == null ? 'Super Admin' : 'Organisation'}
                    </span>
                ),
        }] : []),

        // "Organisation" – SUPER_ADMIN only
        ...(isSuperAdmin ? [{
            key: 'organisation_name',
            header: 'Organisation',
            render: (_: any, row: Role) =>
                row.organisation_name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
        }] : []),

        {
            key: 'statusCode',
            header: 'Status',
            render: (value: string | null) => {
                const isActive = value === 'ACTIVE';
                return (
                    <span className={`${styles.statusBadge} ${isActive ? styles.active : styles.inactive}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                );
            },
        },
        {
            key: 'users_count',
            header: 'Users Count',
            render: (value: number, row: Role) => (
                <span
                    className={`${styles.userCountBadge} ${value > 0 ? styles.clickable : styles.empty}`}
                    onClick={() => value > 0 && handleUsersClick(row)}
                >
                    {value}
                </span>
            ),
        },

        // Actions column – only if at least one action is permitted
        ...(canEditRole || canToggleRole ? [{
            key: 'actions',
            header: 'Actions',
            render: (_: any, row: Role) => (
                <div style={{ display: 'flex', gap: '8px' }}>

                        <Tooltip
                            content={!row.can_edit ? 'Cannot edit system role' : 'Edit'}
                            preferredPosition="left"
                        >
                            <button
                                className={`${styles.actionBtn} ${styles.edit}`}
                                onClick={() => handleEdit(row)}
                                style={{
                                    opacity: row.can_edit ? 1 : 0.5,
                                    cursor:  row.can_edit ? 'pointer' : 'not-allowed',
                                }}
                            >
                                <Edit2 size={14} />
                            </button>
                        </Tooltip>

                        <Tooltip
                            content={
                                !row.can_edit
                                    ? 'Cannot modify system role'
                                    : row.statusCode === 'ACTIVE' ? 'Deactivate' : 'Activate'
                            }
                            preferredPosition="left"
                        >
                            <button
                                className={`${styles.actionBtn} ${row.statusCode === 'ACTIVE' ? styles.deactivate : styles.activate}`}
                                onClick={() => handleToggleStatus(row)}
                                style={{
                                    opacity: row.can_edit ? 1 : 0.5,
                                    cursor:  row.can_edit ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {row.statusCode === 'ACTIVE'
                                    ? <StopCircle size={14} />
                                    : <PlayCircle size={14} />}
                            </button>
                        </Tooltip>
                    
                </div>
            ),
        }] : []),
    ];

    // ─────────────────────────────────────────────────────────────────────────
    // EARLY RETURNS
    // ─────────────────────────────────────────────────────────────────────────
    if (!permissionsLoaded || isInitialLoading) {
        return <Loading message="Loading roles..." />;
    }

    if (!canViewRole) {
        return (
            <div className="management-content">
                <div style={{ textAlign: 'center', padding: '80px', color: '#64748b' }}>
                    <h2>No Access</h2>
                    <p>You do not have permission to view roles.</p>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className={styles.managementContent}>
            {/* ── Stat Cards ── */}
            <div className={styles.statsGrid}>
                {roleStats.map((stat, i) => (
                    <div
                        key={i}
                        className={`${styles.statCard} ${styles[stat.color]} ${styles.clickable} ${
                            stat.active ? styles.selected : ""
                        }`}
                        onClick={stat.onClick}
                        style={{ cursor: "pointer" }}
                    >
                        <div className={styles.statIcon}>
                            <stat.icon size={16} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>{stat.value}</h3>
                            <p>{stat.title}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Table Section ── */}
            <div className={styles.tableSection} style={{ position: "relative" }}>
                <div className={styles.tableHeader}>
                    <h2>
                        <Shield
                            size={18}
                            style={{ marginRight: "8px", verticalAlign: "middle" }}
                        />
                        Roles
                    </h2>

                    <div
                        style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "center",
                            marginLeft: "auto",
                        }}
                    >
                        {/* Search */}
                        <div style={{ position: "relative", width: "240px" }}>
                            <Search
                                size={16}
                                style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#9ca3af",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search by role name..."
                                className={styles.filterInput}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    style={{
                                        position: "absolute",
                                        right: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#9ca3af",
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Org Filter – SUPER_ADMIN only */}
                        {canFilterOrg && (
                            <button
                                className={`${styles.filterButton} ${
                                    selectedOrg.length > 0 ? styles.active : ""
                                }`}
                                onClick={handleOpenFilters}
                            >
                                <Filter size={14} /> Filters
                                {selectedOrg.length > 0 && (
                                    <span className={styles.filterBadge}>1</span>
                                )}
                            </button>
                        )}

                        {/* Add Role – permission-gated */}
                        {canCreateRole && (
                            <button className={styles.addBtn} onClick={handleAddNew}>
                                <Plus size={14} />
                                Add Role
                            </button>
                        )}
                    </div>
                </div>

                <Table columns={roleColumns} data={roles} maxHeight="calc(100vh - 360px)" />

                {loading && !isInitialLoading && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(255,255,255,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 10,
                    }}>
                        <Loading message="Updating..." />
                    </div>
                )}
            </div>

            <CommonPagination
                show={totalRoles > itemsPerPage}
                pageCount={Math.ceil(totalRoles / itemsPerPage)}
                currentPage={currentPage}
                totalItems={totalRoles}
                itemsPerPage={itemsPerPage}
                onPageChange={(data) => setCurrentPage(data.selected)}
                onItemsPerPageChange={(items) => { setItemsPerPage(items); setCurrentPage(0); }}
            />

            {/* ── Filter Offcanvas (SUPER_ADMIN only) ── */}
            {canFilterOrg && (
                <div
                    className={`${styles.offcanvas} ${
                        showFilters ? styles.offcanvasOpen : ""
                    }`}
                >
                    <div
                        className={styles.offcanvasOverlay}
                        onClick={() => setShowFilters(false)}
                    />
                    <div className={styles.offcanvasContent}>
                        <div className={styles.offcanvasHeader}>
                            <h3>Filters</h3>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowFilters(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.offcanvasBody}>
                            <div className={styles.filterGroup}>
                                <label>Organisation</label>
                                <CommonMultiSelect
                                    options={orgOptions}
                                    value={selectedOrg}
                                    onChange={setSelectedOrg}
                                    placeholder="Filter by Organisation"
                                    size="md"
                                />
                            </div>
                        </div>
                        <div className={styles.offcanvasFooter}>
                            <button
                                className={styles.resetButton}
                                onClick={() => setSelectedOrg([])}
                                disabled={selectedOrg.length === 0}
                            >
                                Reset
                            </button>
                            <button
                                className={styles.applyButton}
                                onClick={() => setShowFilters(false)}
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modals ── */}
            <RoleModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                initialData={editingRole ? { name: editingRole.name, description: editingRole.description || '', id: editingRole.id } : undefined}
                title={editingRole ? 'Edit Role' : 'Add New Role'}
                modules={modulesList}
                privileges={privileges}
            />

            <RoleUsersModal
                isOpen={usersModalOpen}
                onClose={() => setUsersModalOpen(false)}
                roleId={selectedRoleForUsers?.id || ''}
                roleName={selectedRoleForUsers?.name || ''}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, role: null })}
                onConfirm={handleConfirmDeactivate}
                title="Deactivate Role"
                message={
                    confirmModal.role?.users_count
                        ? `Deactivating this role will also affect ${confirmModal.role.users_count} user(s). Are you sure?`
                        : 'Are you sure you want to deactivate this role?'
                }
                confirmText="Deactivate"
                type="warning"
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default RoleManagement;